import { create } from 'zustand';

export type BillingDialog =
  | 'subscriptionWizard'
  | 'resumeSubscription'
  | 'addSubAccount'
  | 'enableTeamBilling'
  | 'upgradeRequired'
  | 'addonRequired'
  | 'addonUpdate'
  | 'unpaidInvoices'
  | 'billingInformation';

interface BillingDialogState {
  activeDialog: BillingDialog | null;
  dialogContext: Record<string, unknown>;
  openDialog: (dialog: BillingDialog, context?: Record<string, unknown>) => void;
  closeDialog: () => void;
  /**
   * SP53 — ScreenManagement is an OVERLAY dialog (not part of activeDialog enum) so it can
   * stack on top of the subscription wizard. scio1 parity: clicking Select Free with too
   * many screens opens screenManagementModal WITHOUT closing the underlying Update Subscription
   * Plan wizard, letting the user remove screens and immediately retry.
   */
  screenManagementOpen: boolean;
  screenManagementContext: Record<string, unknown>;
  openScreenManagement: (context?: Record<string, unknown>) => void;
  closeScreenManagement: () => void;
  /**
   * Downgrade-to-Free confirm is an OVERLAY (not part of activeDialog enum) so it stacks on
   * top of the subscription wizard. Cancelling the confirm leaves the wizard intact instead
   * of dropping the user back to the SubscriptionPage.
   */
  downgradeOpen: boolean;
  downgradeContext: Record<string, unknown>;
  openDowngrade: (context?: Record<string, unknown>) => void;
  closeDowngrade: () => void;
  /**
   * Pre-confirm feature-comparison overlay shown when the user selects Free from the wizard.
   * Sits in front of the wizard; "Yes, downgrade" hands off to DowngradeDialog (paid) or
   * runs the trial mutation directly. "Choose a different plan" just closes the overlay,
   * leaving the wizard's plan step intact.
   */
  downgradeComparisonOpen: boolean;
  downgradeComparisonContext: Record<string, unknown>;
  openDowngradeComparison: (context?: Record<string, unknown>) => void;
  closeDowngradeComparison: () => void;
  /**
   * "Why are you downgrading?" reason survey shown BEFORE the feature-comparison overlay
   * when the user selects Free from the wizard. Captures the reason + a free-text note;
   * "Downgrade to free" hands off to DowngradeComparisonDialog, "Keep current plan"
   * closes the overlay and leaves the wizard plan-step intact.
   */
  downgradeSurveyOpen: boolean;
  downgradeSurveyContext: Record<string, unknown>;
  openDowngradeSurvey: (context?: Record<string, unknown>) => void;
  closeDowngradeSurvey: () => void;
  /**
   * #1460 — Member overflow warning shown when a trial account has >= FREE_PLAN_LIMITS.MAX_USER
   * members and tries to downgrade to Free. Opens BEFORE the survey as a standalone dialog so
   * the user sees the blocker immediately without going through the full downgrade flow.
   */
  memberDowngradeWarningOpen: boolean;
  memberDowngradeWarningCount: number;
  openMemberDowngradeWarning: (memberCount: number) => void;
  closeMemberDowngradeWarning: () => void;
  /**
   * Cancel-flow source — set when the user advances out of the cancel-sub
   * dialog into a wizard/downgrade flow, identifying which CTA they took:
   *   - `'switch_plans'`     — picked "I want to switch plans" → wizard
   *   - `'too_expensive'`    — picked "It's too expensive" → retention offer
   *                            ("Switch to Standard" / "Switch to Pro Plus"
   *                            via wizard, or "Switch to Free" via downgrade)
   * Read by useSubscriptionWizard + DowngradeComparisonDialog on successful
   * completion to write a userSurveys row attributing the change to the
   * cancel flow (formType `cancelSwitchPlans` or `cancelDowngrade`). Cleared
   * on closeDialog or after the row writes.
   */
  cancelFlowSource: 'switch_plans' | 'too_expensive' | null;
  setCancelFlowSource: (v: 'switch_plans' | 'too_expensive' | null) => void;
}

export const useBillingDialogStore = create<BillingDialogState>((set) => ({
  activeDialog: null,
  dialogContext: {},
  openDialog: (dialog, context = {}) => set({ activeDialog: dialog, dialogContext: context }),
  closeDialog: () => set({ activeDialog: null, dialogContext: {}, cancelFlowSource: null }),
  screenManagementOpen: false,
  screenManagementContext: {},
  openScreenManagement: (context = {}) =>
    set({ screenManagementOpen: true, screenManagementContext: context }),
  closeScreenManagement: () => set({ screenManagementOpen: false, screenManagementContext: {} }),
  downgradeOpen: false,
  downgradeContext: {},
  openDowngrade: (context = {}) => set({ downgradeOpen: true, downgradeContext: context }),
  closeDowngrade: () => set({ downgradeOpen: false, downgradeContext: {} }),
  downgradeComparisonOpen: false,
  downgradeComparisonContext: {},
  openDowngradeComparison: (context = {}) =>
    set({ downgradeComparisonOpen: true, downgradeComparisonContext: context }),
  closeDowngradeComparison: () =>
    set({ downgradeComparisonOpen: false, downgradeComparisonContext: {} }),
  downgradeSurveyOpen: false,
  downgradeSurveyContext: {},
  openDowngradeSurvey: (context = {}) =>
    set({ downgradeSurveyOpen: true, downgradeSurveyContext: context }),
  closeDowngradeSurvey: () => set({ downgradeSurveyOpen: false, downgradeSurveyContext: {} }),
  memberDowngradeWarningOpen: false,
  memberDowngradeWarningCount: 0,
  openMemberDowngradeWarning: (memberCount) =>
    set({ memberDowngradeWarningOpen: true, memberDowngradeWarningCount: memberCount }),
  closeMemberDowngradeWarning: () =>
    set({ memberDowngradeWarningOpen: false, memberDowngradeWarningCount: 0 }),
  cancelFlowSource: null,
  setCancelFlowSource: (v) => set({ cancelFlowSource: v }),
}));

/** Open the subscription wizard in upgrade mode — skips to plan step, filters plans >= minPlan */
export function openUpgradeWizard(minPlanRequired: string) {
  useBillingDialogStore.getState().openDialog('subscriptionWizard', {
    preSelectedPlan: minPlanRequired,
    minPlanRequired,
  });
}
